import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'usersAccount',
  initialState,
  reducers: {
    someAction(state, action: PayloadAction<any>) {},
  },
});

export const api = createApi({
  reducerPath: 'usersAccountApi',
  tagTypes: ['user', 'package', 'usersList'],
  baseQuery,
  endpoints: builder => ({
    getUsersAccountOrder: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/orders`,
        method: 'GET',
        params,
      }),
    }),
    getStats: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/profile/stats`,
        method: 'GET',
        params: params,
      }),
    }),

    getUsersAccountCourse: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/courses`,
        method: 'GET',
        params: params,
      }),
    }),
    getTransactions: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/transactions`,
        method: 'GET',
        params: params,
      }),
    }),
    getWalletTransactions: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/wallet/transactions`,
        method: 'GET',
        params: params,
      }),
    }),
    getUsersAccountPackages: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/packages`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['package'],
    }),
    getPackageUsage: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/packages/usage`,
        method: 'GET',
        params: params,
      }),
    }),
    getUsersAccountDownload: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/downloads`,
        method: 'GET',
        params,
      }),
    }),
    getProfile: builder.query<any, any>({
      query: ({ userId }) => ({
        url: `${endpoints.USERS_ACCOUNT}/profile`,
        method: 'GET',
        params: { userId },
      }),
      providesTags: ['user'],
    }),
    updateProfile: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/profile/update`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['user', 'usersList'],
    }),
    addCredits: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/wallet/add-credits`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    transferBalance: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/wallet/transfer-balance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    updatePackage: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/packages/update`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['user'],
    }),
    addPackage: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/packages/add`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['package'],
    }),
    changePassword: builder.mutation({
      query: data => ({
        url: `${endpoints.USERS_ACCOUNT}/profile/change-password`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteAccount: builder.mutation({
      query: id => ({
        url: `${endpoints.USERS_ACCOUNT}/profile/delete`,
        method: 'DELETE',
        body: { user_id: id },
      }),
      invalidatesTags: ['user'],
    }),
    recentOrders: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/orders/recent`,
        method: 'GET',
        params: params,
      }),
    }),
    recentDownloads: builder.query<any, any>({
      query: params => ({
        url: `${endpoints.USERS_ACCOUNT}/downloads/recent`,
        method: 'GET',
        params: params,
      }),
    }),
  }),
});

export const { actions: UserAccountActions } = slice;

export const useUserAccountSlice = () => {
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  //  useInjectSaga({ key: slice.name, saga: Saga });
  return {
    actions: UserAccountActions,
    useGetProfileQuery: api.useLazyGetProfileQuery,
    useLazyGetTransactionsQuery: api.useLazyGetTransactionsQuery,
    useLazyGetStatsQuery: api.useLazyGetStatsQuery,
    useGetOrdersQuery: api.useLazyGetUsersAccountOrderQuery,
    useLazyGetUsersAccountCourseQuery: api.useLazyGetUsersAccountCourseQuery,
    useLazyGetWalletTransactionsQuery: api.useLazyGetWalletTransactionsQuery,
    useLazyGetUsersAccountPackagesQuery:
      api.useLazyGetUsersAccountPackagesQuery,
    useLazyGetUsersAccountDownloadQuery:
      api.useLazyGetUsersAccountDownloadQuery,
    useAddCreditsMutation: api.useAddCreditsMutation,
    useGetPackageUsageQuery: api.useLazyGetPackageUsageQuery,
    useUpdatePackageMutation: api.useUpdatePackageMutation,
    useAddPackageMutation: api.useAddPackageMutation,
    useUpdateProfileMutation: api.useUpdateProfileMutation,
    useChangePasswordMutation: api.useChangePasswordMutation,
    useTransferBalanceMutation: api.useTransferBalanceMutation,
    useDeleteAccountMutation: api.useDeleteAccountMutation,
    useGetRecentOrdersQuery: api.useLazyRecentOrdersQuery,
    useGetRecentDownloadsQuery: api.useLazyRecentDownloadsQuery,
  };
};
